import { PROJECT_TYPE_LABELS, type ProjectType } from "@reno-estimator/core";

/**
 * UI-only display metadata: grouping, icons, and which project types
 * currently have a working calculation engine methodology behind them.
 * This should track packages/core/src/calculation/rules/index.ts's
 * PROJECT_CALCULATORS registry (plus real pricing data behind each one) -
 * as of Phase 20, every project type has one except "other_custom". This
 * is presentation grouping only; it does not change what the engine
 * computes. Every project type is still selectable, and unsupported ones
 * get an honest "not available yet" result instead of a fabricated
 * number - see Step7Estimate.
 */
export const SUPPORTED_PROJECT_TYPES: ReadonlySet<ProjectType> = new Set([
  "kitchen",
  "bathroom",
  "ensuite",
  "basement",
  "basement_suite",
  "addition",
  "deck_patio",
  "living_room",
  "family_room",
  "dining_room",
  "bedroom",
  "primary_bedroom",
  "home_office",
  "laundry_room",
  "mudroom",
  "entryway_foyer",
  "hallway",
  "garage",
  "attic",
  "exterior",
  "whole_home_renovation"
]);

export interface ProjectTypeOption {
  id: ProjectType;
  label: string;
  icon: string;
  supported: boolean;
}

const POPULAR: ProjectType[] = ["kitchen", "bathroom", "ensuite", "basement", "basement_suite", "whole_home_renovation", "addition"];

function toOption(id: ProjectType): ProjectTypeOption {
  return { id, label: PROJECT_TYPE_LABELS[id], icon: ICONS[id] ?? "🏠", supported: SUPPORTED_PROJECT_TYPES.has(id) };
}

const ICONS: Partial<Record<ProjectType, string>> = {
  kitchen: "🍳",
  bathroom: "🛁",
  ensuite: "🚿",
  basement: "🧱",
  basement_suite: "🏚️",
  living_room: "🛋️",
  family_room: "📺",
  dining_room: "🍽️",
  bedroom: "🛏️",
  primary_bedroom: "🛏️",
  laundry_room: "🧺",
  mudroom: "👢",
  home_office: "💻",
  garage: "🚗",
  entryway_foyer: "🚪",
  hallway: "➡️",
  attic: "🪜",
  whole_home_renovation: "🏡",
  addition: "➕",
  exterior: "🏘️",
  deck_patio: "🌳",
  other_custom: "✨"
};

export const POPULAR_PROJECT_TYPE_OPTIONS: ProjectTypeOption[] = POPULAR.map(toOption);

export const ALL_PROJECT_TYPE_OPTIONS: ProjectTypeOption[] = (Object.keys(PROJECT_TYPE_LABELS) as ProjectType[]).map(toOption);
